import {useGetOrganizationsQuery} from '@apis/infrastructureTenants'
import type {BaseFetchResultsInterface} from '@interfaces/base'
import type {TenantDetailInterface} from '@interfaces/tenants'

/**
 * Where a signed-in user stands with organizations (AB#6851):
 * - `active`: at least one ACTIVE organization.
 * - `none`: no ACTIVE one. Since AB#6814 a sign-up from an unknown domain gets
 *   no organization at all, and organizations are registered only by staff
 *   (AB#6862), so only an invitation or an approved join request helps:
 *   /no-organization.
 * - `undefined` while loading.
 */
export type OrganizationAccess = 'active' | 'none'

export const useOrganizationAccess = (
  skip = false,
): {
  activeOrganizations: BaseFetchResultsInterface<TenantDetailInterface> | undefined
  access: OrganizationAccess | undefined
} => {
  const {data: activeOrganizations, isFetching} = useGetOrganizationsQuery(
    {limit: 1, offset: 0, ordering: 'name', status: 'ACTIVE'},
    {skip},
  )

  let access: OrganizationAccess | undefined
  if (activeOrganizations && activeOrganizations.count > 0) {
    access = 'active'
  } else if (activeOrganizations && !isFetching) {
    // Not mid-refetch: a stale empty page (say, right after an approval or an
    // organization switch) must not send the user away.
    access = 'none'
  }

  return {activeOrganizations, access}
}
